import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'react-toastify';
import { submitSupportRequest } from '../services/api';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.6;
`;

const SupportPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    email: user?.email || '',
    contactReason: '',
    orderNumber: '',
    query: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }
    
    if (!formData.contactReason) {
      toast.error('Please select a reason for contacting us');
      return;
    }
    
    if (!formData.query.trim()) {
      toast.error('Please enter your query');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await submitSupportRequest(formData);
      if (response.success) {
        toast.success('Support request submitted successfully! We will get back to you soon.');
        setFormData({
          email: user?.email || '',
          contactReason: '',
          orderNumber: '',
          query: '',
        });
      } else {
        toast.error(response.error || 'Failed to submit support request');
      }
    } catch (error) {
      toast.error('Failed to submit support request. Please try again.');
      console.error('Support request error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <SEO 
        title="Support & Help"
        description="Get help and support from TheMadZone. Contact our customer service team, find answers to common questions, and get assistance with orders, products, and account issues."
        keywords="support, help, customer service, contact, TheMadZone support, help center"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Support & Help</Title>
        <Subtitle>
          Have a question or need assistance? Fill out the form below and we'll get back to you as soon as possible.
        </Subtitle>

        <Card>
          <CardHeader>
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactReason">What are you contacting us about? *</Label>
                <Select
                  value={formData.contactReason}
                  onValueChange={(value) => handleSelectChange('contactReason', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order-inquiry">Order Inquiry</SelectItem>
                    <SelectItem value="order-problem">Order Problem</SelectItem>
                    <SelectItem value="shipping-question">Shipping Question</SelectItem>
                    <SelectItem value="return-refund">Return/Refund</SelectItem>
                    <SelectItem value="product-question">Product Question</SelectItem>
                    <SelectItem value="account-issue">Account Issue</SelectItem>
                    <SelectItem value="payment-issue">Payment Issue</SelectItem>
                    <SelectItem value="technical-support">Technical Support</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="orderNumber">Order Number (Optional)</Label>
                <Input
                  id="orderNumber"
                  name="orderNumber"
                  type="text"
                  value={formData.orderNumber}
                  onChange={handleChange}
                  placeholder="e.g., ORD-123456"
                />
                <p className="text-sm text-gray-500">
                  If your inquiry is related to an order, please include your order number for faster assistance.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="query">Your Message *</Label>
                <Textarea
                  id="query"
                  name="query"
                  value={formData.query}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Please describe your question or issue in detail..."
                  className="resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div style={{ marginTop: '40px', padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Other Ways to Reach Us</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '8px' }}>
            <strong>Email:</strong> hello@themadzone.com
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '12px' }}>
            We typically respond within 24-48 hours during business days.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
            <strong>Need quick answers?</strong> Check out our{' '}
            <Link to="/faq" style={{ color: '#000', textDecoration: 'underline', fontWeight: 500 }}>
              Frequently Asked Questions (FAQs)
            </Link>
            {' '}for common questions and solutions.
          </p>
        </div>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SupportPage;

